import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Tag } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const SUBCATEGORIES_COLLECTION = 'subcategories';
const CATEGORIES_COLLECTION = 'categories';

export default function SubcategoryManagement() {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    order: 0
  });
  const { showToast } = useToast();

  useEffect(() => {
    loadCategories();
    loadSubcategories();
  }, []);

  const loadCategories = async () => {
    try {
      const result = await databaseService.listDocuments(CATEGORIES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(SUBCATEGORIES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setSubcategories(sorted);
      }
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      const subcategoryData = {
        name: formData.name.trim(),
        categoryId: formData.categoryId,
        order: parseInt(formData.order) || subcategories.length
      };

      let result;
      if (editingSubcategory) {
        result = await databaseService.updateDocument(
          SUBCATEGORIES_COLLECTION,
          editingSubcategory.$id,
          subcategoryData
        );
      } else {
        result = await databaseService.createDocument(
          SUBCATEGORIES_COLLECTION,
          subcategoryData
        );
      }

      if (result.success) {
        showToast(
          editingSubcategory ? 'Subcategory updated successfully' : 'Subcategory created successfully',
          'success'
        );
        resetForm();
        loadSubcategories();
      } else {
        showToast('Failed to save subcategory: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving subcategory: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      categoryId: subcategory.categoryId,
      order: subcategory.order
    });
    setShowForm(true);
  };

  const handleDelete = async (subcategoryId, subcategoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${subcategoryName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const result = await databaseService.deleteDocument(
        SUBCATEGORIES_COLLECTION,
        subcategoryId
      );
      if (result.success) {
        showToast('Subcategory deleted successfully', 'success');
        loadSubcategories();
      } else {
        showToast('Failed to delete subcategory', 'error');
      }
    } catch (error) {
      showToast('Error deleting subcategory', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', categoryId: '', order: 0 });
    setEditingSubcategory(null);
    setShowForm(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.$id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(cat => cat.$id === categoryId);
    return category ? category.color : '#3b82f6';
  };

  // Group subcategories by category
  const groupedSubcategories = categories.map(category => ({
    category,
    subcategories: subcategories.filter(sub => sub.categoryId === category.$id)
  }));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
            Subcategory Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage project subcategories under each category
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105"
          style={{ backgroundColor: '#4C1D95' }}
        >
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {showForm ? 'Cancel' : 'Add Subcategory'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#4C1D95' }}>
            {editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Parent Category *
              </label>
              {categories.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    No categories found. Please create categories first.
                  </p>
                </div>
              ) : (
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#4C1D95' }}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.$id} value={cat.$id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Subcategory Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                maxLength="50"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#4C1D95' }}
                placeholder="e.g., Seismic Analysis, Urban Planning"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Display Order
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                min="0"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#4C1D95' }}
                placeholder="0"
              />
              <p className="text-sm text-gray-500 mt-1">
                Lower numbers appear first in the filter list
              </p>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Save className="w-5 h-5" />
                {loading ? 'Saving...' : editingSubcategory ? 'Update Subcategory' : 'Create Subcategory'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 rounded-lg border-2 transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Subcategories List - Grouped by Category */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
          All Subcategories ({subcategories.length})
        </h2>
        
        {loading && !showForm ? (
          <p>Loading subcategories...</p>
        ) : subcategories.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No subcategories yet</p>
            <p className="text-gray-400 text-sm">Add your first subcategory to get started</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedSubcategories.map(({ category, subcategories: subs }) => (
              subs.length > 0 && (
                <div key={category.$id} className="border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="px-4 py-2 rounded-full text-white font-semibold text-sm"
                      style={{ backgroundColor: category.color }}
                    >
                      {category.name}
                    </div>
                    <span className="text-sm text-gray-500">
                      ({subs.length} subcategories)
                    </span>
                  </div>
                  
                  <div className="space-y-2 ml-4">
                    {subs.map((subcategory) => (
                      <div
                        key={subcategory.$id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:shadow-md transition-shadow bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{subcategory.name}</span>
                          <span className="text-xs text-gray-500">
                            Order: {subcategory.order}
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(subcategory)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg text-white text-sm transition-all duration-300 hover:scale-105"
                            style={{ backgroundColor: '#4C1D95' }}
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(subcategory.$id, subcategory.name)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500 text-white text-sm transition-all duration-300 hover:scale-105"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
